import { visit } from "unist-util-visit";

/**
 * Semantic concept names for the emoji Ask Puntt emits in chat markdown. The
 * renderer (`Markdown.tsx`) maps each concept to a vibe-matched, colored
 * icon-library glyph so the assistant's emoji read as on-brand inline icons
 * instead of raw OS emoji.
 *
 * Two families live here:
 * - Status glyphs (colored circles, checks, crosses, warnings) — several
 *   look-alike codepoints collapse onto one status concept.
 * - Object / semantic emoji (wave, palette, clipboard, magnifier, chart,
 *   rocket, etc.) — each maps to a concept that resolves to an equivalent
 *   icon so meaning survives the substitution.
 *
 * `neutral` is the LAST-RESORT bucket for any pictographic emoji that is NOT
 * in the table below — it keeps stray/unforeseen emoji from rendering as raw
 * OS glyphs while still reading as an intentional inline marker. The common
 * emoji set above must all resolve to a real, specific icon; only genuinely
 * unknown emoji fall through to `neutral`.
 */
export type EmojiConcept =
  // Status family.
  | "alert"
  | "ok"
  | "caution"
  | "info"
  | "warning"
  | "approved"
  | "error"
  | "blocked"
  | "question"
  | "neutral"
  // Gestures / people.
  | "wave"
  | "applause"
  | "thumbsUp"
  | "thumbsDown"
  | "thanks"
  | "strength"
  | "handshake"
  | "writing"
  | "eyes"
  // Objects / tools.
  | "palette"
  | "clipboard"
  | "folder"
  | "folderOpen"
  | "files"
  | "document"
  | "note"
  | "search"
  | "link"
  | "attachment"
  | "pin"
  | "barChart"
  | "chartUp"
  | "chartDown"
  | "image"
  | "tag"
  | "settings"
  | "tools"
  | "wrench"
  | "key"
  | "puzzle"
  | "package"
  | "rocket"
  | "idea"
  | "notification"
  | "clock"
  | "timer"
  | "hourglass"
  | "calendar"
  | "target"
  | "finish"
  | "save"
  | "cleanup"
  | "experiment"
  | "microscope"
  | "web"
  | "legal"
  | "delete"
  | "location"
  | "comment"
  | "thought"
  | "bot"
  | "bug"
  | "company"
  | "home"
  | "construction"
  | "camera"
  | "measure"
  | "books"
  | "bookmarks"
  | "locked"
  | "secure"
  | "ticket"
  | "numbers"
  | "announcement"
  // Symbols.
  | "star"
  | "sparkle"
  | "fire"
  | "celebration";

/**
 * @deprecated Use `EmojiConcept`. Retained as an alias so existing imports keep
 * compiling while the broader concept map supersedes the status-only naming.
 */
export type EmojiStatusName = EmojiConcept;

interface EmojiEntry {
  /** The base emoji code point, written as a Unicode escape. */
  char: string;
  name: EmojiConcept;
}

/**
 * Single source of truth: codepoint -> concept. Emoji are written as Unicode
 * escapes (never literal glyphs) to satisfy the repo's "no emojis in new code"
 * rule. A trailing variation selector (U+FE0F) is matched optionally at scan
 * time so the text and emoji presentation variants collapse onto the same icon.
 *
 * Adding a future emoji is one line here (plus a presentation row in
 * `Markdown.tsx` only when introducing a brand-new concept).
 */
export const EMOJI_ENTRIES: EmojiEntry[] = [
  // --- Status: colored-circle family (severity badges + neutral dots). ---
  { char: "\u{1F534}", name: "alert" }, // large red circle (high severity / alert)
  { char: "\u{1F7E0}", name: "caution" }, // large orange circle
  { char: "\u{1F7E1}", name: "caution" }, // large yellow circle (medium severity)
  { char: "\u{1F7E2}", name: "ok" }, // large green circle (low severity / good)
  { char: "\u{1F535}", name: "info" }, // large blue circle (out-of-scope / informational)
  { char: "\u{1F7E3}", name: "info" }, // large purple circle
  { char: "\u{1F7E4}", name: "neutral" }, // large brown circle
  { char: "\u{26AB}", name: "neutral" }, // medium black circle
  { char: "\u{26AA}", name: "neutral" }, // medium white circle
  // --- Status: check / approval family. ---
  { char: "\u{2705}", name: "approved" }, // white heavy check mark
  { char: "\u{2714}", name: "approved" }, // heavy check mark
  { char: "\u{2713}", name: "approved" }, // check mark
  { char: "\u{2611}", name: "approved" }, // ballot box with check
  // --- Status: cross / error family. ---
  { char: "\u{274C}", name: "error" }, // cross mark
  { char: "\u{274E}", name: "error" }, // negative squared cross mark
  { char: "\u{2716}", name: "error" }, // heavy multiplication x
  { char: "\u{2717}", name: "error" }, // ballot x
  { char: "\u{2718}", name: "error" }, // heavy ballot x
  // --- Status: warning / prohibited family. ---
  { char: "\u{26A0}", name: "warning" }, // warning sign (triangle)
  { char: "\u{2757}", name: "warning" }, // exclamation mark
  { char: "\u{2755}", name: "warning" }, // white exclamation mark
  { char: "\u{1F6AB}", name: "blocked" }, // no entry sign (prohibited)
  { char: "\u{26D4}", name: "blocked" }, // no entry
  // --- Status: info / question family. ---
  { char: "\u{2139}", name: "info" }, // information source
  { char: "\u{2753}", name: "question" }, // question mark
  { char: "\u{2754}", name: "question" }, // white question mark
  // --- Gestures / people. ---
  { char: "\u{1F44B}", name: "wave" }, // waving hand
  { char: "\u{1F64C}", name: "applause" }, // raising hands
  { char: "\u{1F44F}", name: "applause" }, // clapping hands
  { char: "\u{1F44D}", name: "thumbsUp" }, // thumbs up
  { char: "\u{1F44E}", name: "thumbsDown" }, // thumbs down
  { char: "\u{1F64F}", name: "thanks" }, // folded hands (thanks/please)
  { char: "\u{1F4AA}", name: "strength" }, // flexed biceps
  { char: "\u{1F91D}", name: "handshake" }, // handshake
  { char: "\u{270D}", name: "writing" }, // writing hand
  { char: "\u{1F440}", name: "eyes" }, // eyes
  // --- Objects / tools. ---
  { char: "\u{1F3A8}", name: "palette" }, // artist palette
  { char: "\u{1F4CB}", name: "clipboard" }, // clipboard
  { char: "\u{1F4C1}", name: "folder" }, // file folder
  { char: "\u{1F4C2}", name: "folderOpen" }, // open file folder
  { char: "\u{1F5C2}", name: "files" }, // card index dividers
  { char: "\u{1F4C4}", name: "document" }, // page facing up
  { char: "\u{1F4DD}", name: "note" }, // memo
  { char: "\u{1F50D}", name: "search" }, // magnifying glass (left)
  { char: "\u{1F50E}", name: "search" }, // magnifying glass (right)
  { char: "\u{1F517}", name: "link" }, // link
  { char: "\u{1F4CE}", name: "attachment" }, // paperclip
  { char: "\u{1F4CC}", name: "pin" }, // pushpin
  { char: "\u{1F4CA}", name: "barChart" }, // bar chart
  { char: "\u{1F4C8}", name: "chartUp" }, // chart increasing
  { char: "\u{1F4C9}", name: "chartDown" }, // chart decreasing
  { char: "\u{1F5BC}", name: "image" }, // framed picture
  { char: "\u{1F3F7}", name: "tag" }, // label/tag
  { char: "\u{2699}", name: "settings" }, // gear
  { char: "\u{1F6E0}", name: "tools" }, // hammer and wrench
  { char: "\u{1F527}", name: "wrench" }, // wrench
  { char: "\u{1F511}", name: "key" }, // key
  { char: "\u{1F9E9}", name: "puzzle" }, // puzzle piece
  { char: "\u{1F4E6}", name: "package" }, // package
  { char: "\u{1F680}", name: "rocket" }, // rocket
  { char: "\u{1F4A1}", name: "idea" }, // light bulb (idea/tip)
  { char: "\u{1F514}", name: "notification" }, // bell
  { char: "\u{1F552}", name: "clock" }, // three o'clock
  { char: "\u{23F0}", name: "clock" }, // alarm clock
  { char: "\u{23F1}", name: "timer" }, // stopwatch
  { char: "\u{23F3}", name: "hourglass" }, // hourglass not done
  { char: "\u{231B}", name: "hourglass" }, // hourglass done
  { char: "\u{1F4C5}", name: "calendar" }, // calendar
  { char: "\u{1F3AF}", name: "target" }, // direct hit (target)
  { char: "\u{1F3C1}", name: "finish" }, // chequered flag
  { char: "\u{1F4BE}", name: "save" }, // floppy disk
  { char: "\u{1F9F9}", name: "cleanup" }, // broom
  { char: "\u{1F9EA}", name: "experiment" }, // test tube
  { char: "\u{1F52C}", name: "microscope" }, // microscope
  { char: "\u{1F310}", name: "web" }, // globe with meridians
  { char: "\u{2696}", name: "legal" }, // balance scale
  { char: "\u{1F5D1}", name: "delete" }, // wastebasket
  { char: "\u{1F4CD}", name: "location" }, // round pushpin (location)
  { char: "\u{1F4AC}", name: "comment" }, // speech balloon
  { char: "\u{1F4AD}", name: "thought" }, // thought balloon
  { char: "\u{1F916}", name: "bot" }, // robot
  { char: "\u{1F41B}", name: "bug" }, // bug
  { char: "\u{1F3E2}", name: "company" }, // office building
  { char: "\u{1F3E0}", name: "home" }, // house
  { char: "\u{1F3D7}", name: "construction" }, // building construction
  { char: "\u{1F4F7}", name: "camera" }, // camera
  { char: "\u{1F4F8}", name: "camera" }, // camera with flash
  { char: "\u{1F4CF}", name: "measure" }, // straight ruler
  { char: "\u{1F4D0}", name: "measure" }, // triangular ruler
  { char: "\u{1F4DA}", name: "books" }, // books
  { char: "\u{1F4D6}", name: "books" }, // open book
  { char: "\u{1F4D1}", name: "bookmarks" }, // bookmark tabs
  { char: "\u{1F516}", name: "bookmarks" }, // bookmark
  { char: "\u{1F512}", name: "locked" }, // locked
  { char: "\u{1F510}", name: "secure" }, // locked with key
  { char: "\u{1F3AB}", name: "ticket" }, // ticket
  { char: "\u{1F39F}", name: "ticket" }, // admission tickets
  { char: "\u{1F522}", name: "numbers" }, // input numbers
  { char: "\u{1F4E3}", name: "announcement" }, // megaphone
  { char: "\u{1F4E2}", name: "announcement" }, // loudspeaker
  // --- Symbols. ---
  { char: "\u{2B50}", name: "star" }, // star
  { char: "\u{2728}", name: "sparkle" }, // sparkles
  { char: "\u{1F525}", name: "fire" }, // fire
  { char: "\u{1F389}", name: "celebration" }, // party popper
  { char: "\u{1F38A}", name: "celebration" }, // confetti ball
];

/**
 * @deprecated Use `EMOJI_ENTRIES`. Retained as an alias for existing imports.
 */
export const EMOJI_STATUS_ENTRIES = EMOJI_ENTRIES;

const CHAR_TO_NAME = new Map<string, EmojiConcept>(
  EMOJI_ENTRIES.map((entry) => [entry.char, entry.name]),
);

/** Default concept for any pictographic emoji not explicitly enumerated. */
export const FALLBACK_STATUS: EmojiConcept = "neutral";

/**
 * Typographic pictographs that are Extended_Pictographic but read as text,
 * not as emoji. These are passed through untouched so a trademark or copyright
 * mark never collapses into a neutral icon.
 */
const PASSTHROUGH_PICTOGRAPHS = new Set<string>([
  "\u{00A9}", // copyright
  "\u{00AE}", // registered
  "\u{2122}", // trademark
  "\u{203C}", // double exclamation
  "\u{2049}", // exclamation question mark
]);

/** hast tagName for the inline icon element this plugin inserts. */
export const EMOJI_ICON_TAG = "emoji-icon";

/**
 * Resolve the captured emoji (with an optional trailing FE0F stripped) to a
 * concept, or `null` when it is a pass-through typographic mark.
 */
export function resolveEmojiConcept(match: string): EmojiConcept | null {
  const base = match.replace(/\uFE0F$/, "");
  if (PASSTHROUGH_PICTOGRAPHS.has(base)) return null;
  return CHAR_TO_NAME.get(base) ?? FALLBACK_STATUS;
}

/**
 * @deprecated Use `resolveEmojiConcept`. Retained as an alias for existing
 * imports.
 */
export const resolveEmojiStatus = resolveEmojiConcept;

// Match an enumerated glyph OR any pictographic emoji, plus an optional
// trailing variation selector (U+FE0F). The explicit alternation is required
// because several enumerated dingbats (the heavy check/cross/ballot marks
// U+2713/2714/2716/2717/2718) are NOT `Extended_Pictographic` and would
// otherwise slip through. `\p{Extended_Pictographic}` then catches the rest of
// the emoji vocabulary so the fallback can convert any stray glyph to a neutral
// icon instead of leaving raw OS emoji in the output. Built via the RegExp
// constructor so the Unicode flag passes the project's TS lib target check
// (matching the prior pattern in this file).
const EMOJI_REGEX = new RegExp(
  `(?:${EMOJI_ENTRIES.map((entry) => entry.char).join("|")}|\\p{Extended_Pictographic})\\uFE0F?`,
  "gu",
);

/**
 * rehype plugin that splits text nodes on every enumerated / pictographic emoji
 * and replaces each one with an `<emoji-icon>` element carrying the resolved
 * concept in `properties.emojiName`. Enumerated glyphs resolve to their
 * concept; any other emoji resolves to the neutral fallback; typographic marks
 * (copyright/trademark/etc.) are left as text. `Markdown.tsx` registers a
 * component for that tag to render a colored, accessible icon inline.
 *
 * Follows the existing `rehype-color-chip` / `rehype-superscript` text-node
 * splitter pattern in this directory.
 */
function plugin() {
  return (tree: /* hast.Root */ any) => {
    visit(tree, "text", function (node, index, parent) {
      if (!parent || index === null) return;

      // Skip text we produced on a previous split to avoid reprocessing.
      if (node.data?.processedByIconizeEmoji) return;

      const text = "value" in node && node.type === "text" ? node.value : "";

      // Cheap bail-out before the (stateful, global) regex scan.
      EMOJI_REGEX.lastIndex = 0;
      if (!EMOJI_REGEX.test(text)) return;
      EMOJI_REGEX.lastIndex = 0;

      let match;
      let lastIndex = 0;
      const newChildren: any[] = [];

      while ((match = EMOJI_REGEX.exec(text)) !== null) {
        const name = resolveEmojiConcept(match[0]);

        // Pass-through typographic mark: leave it inside the text run.
        if (name === null) continue;

        if (match.index > lastIndex) {
          newChildren.push({
            type: "text",
            value: text.slice(lastIndex, match.index),
            data: { processedByIconizeEmoji: true },
          });
        }

        newChildren.push({
          type: "element",
          tagName: EMOJI_ICON_TAG,
          properties: { emojiName: name },
          children: [],
          data: { processedByIconizeEmoji: true },
        });

        lastIndex = match.index + match[0].length;
      }

      if (lastIndex < text.length) {
        newChildren.push({
          type: "text",
          value: text.slice(lastIndex),
          data: { processedByIconizeEmoji: true },
        });
      }

      if (newChildren.length > 0) {
        parent.children.splice(index, 1, ...newChildren);
      }
    });
  };
}

export default plugin;
